const MOBILE_MEDIA_QUERY = '(max-width: 749px)';

class StickyBuyButtons {
  /** @type {HTMLElement} */
  #buyButtons;

  /** @type {HTMLElement | null} */
  #clone = null;

  /** @type {IntersectionObserver | null} */
  #buyButtonsObserver = null;

  /** @type {IntersectionObserver | null} */
  #footerObserver = null;

  /** @type {MediaQueryList} */
  #mediaQuery;

  /** @type {boolean} */
  #hiddenByFooter = false;

  /** @type {boolean} */
  #isVisible = false;

  /** @type {boolean} */
  #needsSync = false;

  /** @type {string | null} */
  #productId = null;

  /** @type {AbortController | null} */
  #abortController = null;

  constructor(buyButtons) {
    this.#buyButtons = buyButtons;
    this.#mediaQuery = window.matchMedia(MOBILE_MEDIA_QUERY);
    this.#productId = this.#buyButtons.querySelector('product-form-component')?.dataset.productId || null;
  }

  init() {
    if (this.#buyButtons.dataset.stickyInitialized === 'true') return;
    this.#buyButtons.dataset.stickyInitialized = 'true';

    this.#mediaQuery.addEventListener('change', this.#handleMediaChange);
    this.#handleMediaChange();

    this.#abortController = new AbortController();
    document.addEventListener('variant:update', this.#handleVariantUpdate, {
      signal: this.#abortController.signal,
    });
  }

  destroy() {
    this.#teardownObservers();
    this.#removeClone();
    this.#mediaQuery.removeEventListener('change', this.#handleMediaChange);
    this.#abortController?.abort();
    this.#abortController = null;
    this.#buyButtons.removeAttribute('data-sticky-initialized');
  }

  #handleMediaChange = () => {
    if (!this.#mediaQuery.matches) {
      this.#teardownObservers();
      this.#hideClone();
      return;
    }

    this.#ensureClone();
    this.#setupObservers();
    this.#updateVisibility();
  };

  #ensureClone() {
    if (this.#clone) return;

    const section = this.#buyButtons.closest('.shopify-section');
    if (!section) return;
    const targetContainer = this.#buyButtons.closest('.product-details') || section;

    const clone = this.#createPreparedClone();
    targetContainer.appendChild(clone);
    this.#clone = clone;
  }

  #removeClone() {
    if (!this.#clone) return;
    this.#clone.remove();
    this.#clone = null;
  }

  #createPreparedClone() {
    const clone = /** @type {HTMLElement} */ (this.#buyButtons.cloneNode(true));
    clone.dataset.stickyClone = 'true';
    clone.dataset.stickyVisible = this.#isVisible ? 'true' : 'false';
    clone.classList.add('buy-buttons-block--sticky');
    clone.setAttribute('aria-hidden', this.#isVisible ? 'false' : 'true');

    clone.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));
    clone
      .querySelectorAll('[data-shopify-editor-block], [data-shopify-editor-message]')
      .forEach((node) => {
        node.removeAttribute('data-shopify-editor-block');
        node.removeAttribute('data-shopify-editor-message');
      });

    return clone;
  }

  #syncClone() {
    if (!this.#clone) return;
    const nextClone = this.#createPreparedClone();
    this.#clone.replaceWith(nextClone);
    this.#clone = nextClone;
  }

  #setupObservers() {
    if (this.#buyButtonsObserver) return;

    this.#buyButtonsObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      if (entry.isIntersecting) {
        this.#hiddenByFooter = false;
        this.#hideClone();
        return;
      }

      const rect = entry.target.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top < 0) {
        if (!this.#hiddenByFooter) {
          this.#showClone();
        }
      } else {
        this.#hideClone();
      }
    });

    this.#buyButtonsObserver.observe(this.#buyButtons);

    const footer = document.querySelector('footer');
    if (!footer) return;

    this.#footerObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting) {
          this.#hiddenByFooter = true;
          this.#hideClone();
          return;
        }

        if (this.#hiddenByFooter) {
          this.#hiddenByFooter = false;
          this.#updateVisibility();
        }
      },
      { rootMargin: '200px 0px 0px 0px' }
    );

    this.#footerObserver.observe(footer);
  }

  #teardownObservers() {
    this.#buyButtonsObserver?.disconnect();
    this.#buyButtonsObserver = null;

    this.#footerObserver?.disconnect();
    this.#footerObserver = null;
  }

  #updateVisibility() {
    const rect = this.#buyButtons.getBoundingClientRect();
    if ((rect.bottom < 0 || rect.top < 0) && !this.#hiddenByFooter) {
      this.#showClone();
    } else {
      this.#hideClone();
    }
  }

  #showClone() {
    if (!this.#clone || this.#isVisible) return;

    if (this.#needsSync) {
      this.#syncClone();
      this.#needsSync = false;
    }

    this.#isVisible = true;
    this.#clone.dataset.stickyVisible = 'true';
    this.#clone.setAttribute('aria-hidden', 'false');
  }

  #hideClone() {
    if (!this.#clone || !this.#isVisible) return;
    this.#isVisible = false;
    this.#clone.dataset.stickyVisible = 'false';
    this.#clone.setAttribute('aria-hidden', 'true');
  }

  #handleVariantUpdate = (event) => {
    if (!this.#productId) return;
    const eventProductId = event?.detail?.data?.productId;
    if (eventProductId && String(eventProductId) !== String(this.#productId)) return;

    if (!this.#isVisible) {
      this.#needsSync = true;
      return;
    }

    requestAnimationFrame(() => this.#syncClone());
  };
}

const initializeStickyBuyButtons = () => {
  const buyButtonsBlocks = Array.from(document.querySelectorAll('.buy-buttons-block'))
    .filter((block) => block.dataset.stickyClone !== 'true');

  buyButtonsBlocks.forEach((block) => {
    const instance = new StickyBuyButtons(block);
    block.__stickyBuyButtons = instance;
    instance.init();
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeStickyBuyButtons);
} else {
  initializeStickyBuyButtons();
}

document.addEventListener('shopify:section:load', (event) => {
  const section = event.target;
  if (!section) return;

  const buyButtonsBlocks = section.querySelectorAll('.buy-buttons-block');
  if (!buyButtonsBlocks.length) return;

  buyButtonsBlocks.forEach((block) => {
    if (block.dataset.stickyInitialized === 'true') return;
    const instance = new StickyBuyButtons(block);
    block.__stickyBuyButtons = instance;
    instance.init();
  });
});
